import type { Router } from 'vue-router';

interface ClarityConfig {
  enabled: boolean;
  projectId: string;
  userId?: string | number | null;
  userName?: string | null;
  subdomain?: string | null;
  env?: string | null;
}

declare global {
  interface Window {
    __CLARITY_CONFIG__?: ClarityConfig;
    __clarityInitialized?: boolean;
    vaPrivacyBlocked?: () => boolean;
  }
}

let cachedConfig: ClarityConfig | null = null;
let initPromise: Promise<void> | null = null;
let routerHookRegistered = false;

function readConfig(): ClarityConfig | null {
  if (typeof window === 'undefined') return null;
  if (window.vaPrivacyBlocked?.() || (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return null;
  if (cachedConfig) return cachedConfig;
  const cfg = window.__CLARITY_CONFIG__;
  if (!cfg || !cfg.enabled || !cfg.projectId) return null;
  cachedConfig = cfg;
  return cfg;
}

export function initClarity(): Promise<void> {
  if (!initPromise) initPromise = runInit();
  return initPromise;
}

// PERF: Clarity's own script (scripts.clarity.ms/<ver>/clarity.js, ~25KB) is a
// third-party fetch we do not control — PSI flags it under "Use efficient cache
// lifetimes" (TTL 1d) and its parse/execute lands inside the Lighthouse
// observation window on throttled mobile. On public pages we therefore defer
// the init exactly like the GTM container in shared/_google_tags.html.haml:
// first interaction (pointerdown/keydown/touchstart/scroll) wins, with a 10s
// timer after load as the fallback for fully passive sessions.
//
// NOT requestIdleCallback: rIC fires almost immediately after load, which puts
// the third-party work straight back into the PSI window (that regression is
// documented on the GTM loader — TBT 198ms -> 1,178ms).
//
// Real sessions virtually always interact (scroll counts) long before 10s, and
// Clarity's value is behavioural recording — a session with zero interaction
// has nothing to record anyway.
export function initClarityDeferred(): void {
  if (typeof window === 'undefined') return;
  if (!readConfig()) return; // disabled / no project id -> attach nothing

  const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
  const opts: AddEventListenerOptions = { passive: true };
  let started = false;

  const start = (): void => {
    if (started) return;
    started = true;
    events.forEach((e) => window.removeEventListener(e, start, opts));
    // Staggered +2s: GTM, the Vue islands chunk, Bugsnag and Clarity all key
    // off the same first-interaction events, so without an offset they land on
    // the main thread together — inside the very interaction INP is scoring.
    // Order of importance puts analytics (GTM) and UI (islands) first. The
    // honest cost: not just "the first 2s of the recording" — a visitor who
    // navigates away within ~2s of their first interaction is never recorded
    // on that page at all, and a fast pogo-sticker may never be recorded in
    // the whole session. Accepted: those sessions have near-zero behavioural
    // value. Explicit trackEvent/withClarity calls still init immediately.
    window.setTimeout(() => void initClarity(), 2000);
  };

  events.forEach((e) => window.addEventListener(e, start, opts));

  const timedStart = (): void => {
    window.setTimeout(start, 10000);
  };
  if (document.readyState === 'complete') {
    timedStart();
  } else {
    window.addEventListener('load', timedStart, { once: true });
  }
}

async function runInit(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (window.__clarityInitialized) return;
  const cfg = readConfig();
  if (!cfg) return;

  try {
    const mod = await import('@microsoft/clarity');
    if (!readConfig()) return;
    const Clarity = (mod as { default?: unknown }).default ?? mod;
    const c = Clarity as {
      init: (id: string) => void;
      identify: (id: string, s?: string, p?: string, n?: string) => void;
      setTag: (k: string, v: string) => void;
    };

    c.init(cfg.projectId);
    window.__clarityInitialized = true;

    if (cfg.userId != null) {
      c.identify(String(cfg.userId), undefined, undefined, cfg.userName || undefined);
    }

    if (cfg.subdomain) c.setTag('subdomain', cfg.subdomain);
    if (cfg.env) c.setTag('env', cfg.env);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[clarity] init failed', err);
  }
}

async function withClarity(fn: (c: unknown) => void): Promise<void> {
  if (!readConfig()) return;
  try {
    await initClarity();
    if (!readConfig()) return;
    const mod = await import('@microsoft/clarity');
    if (!readConfig()) return;
    const Clarity = (mod as { default?: unknown }).default ?? mod;
    fn(Clarity);
  } catch {
    /* noop */
  }
}

export function trackPageView(page: string): void {
  void withClarity((c) => (c as { setTag: (k: string, v: string) => void }).setTag('page', page));
}

export function trackEvent(name: string): void {
  void withClarity((c) => (c as { event: (n: string) => void }).event(name));
}

export function setClarityTag(key: string, value: string): void {
  void withClarity((c) => (c as { setTag: (k: string, v: string) => void }).setTag(key, value));
}

export function registerClarityRouterHook(router: Router): void {
  if (!readConfig() || routerHookRegistered) return;
  routerHookRegistered = true;
  router.afterEach((to) => {
    const page = (to.name as string) || to.path;
    trackPageView(page);
  });
}

export function resetClarityForTests(): void {
  cachedConfig = null;
  initPromise = null;
  routerHookRegistered = false;
  if (typeof window !== 'undefined') {
    delete window.__clarityInitialized;
  }
}
